import { httpClient } from '@/api/httpClient';
import {
  mapPaginatedResource,
  type PaginatedApiResource,
  type PaginatedResult,
} from '@/api/pagination.types';
import {
  mapApiResourceToAssistantAskResult,
  mapApiResourceToAssistantConversation,
  mapApiResourceToAssistantMessage,
  mapApiResourceToAssistantMessagePage,
  mapApiResourceToAssistantUiAction,
} from './assistantMapping';
import type {
  AssistantAskApiResource,
  AssistantAskResult,
  AssistantConversation,
  AssistantConversationApiResource,
  AssistantFeedback,
  AssistantFeedbackReason,
  AssistantMessage,
  AssistantMessageApiResource,
  AssistantMessagePage,
  AssistantMessagePageApiResource,
  AssistantPageContext,
  AssistantUiAction,
  AssistantUiActionApiResource,
} from './assistantApi.types';

const ASSISTANT_BASE_PATH = '/assistant';

export async function fetchAssistantConversations(
  page: number,
  pageSize: number,
  search: string
): Promise<PaginatedResult<AssistantConversation>> {
  const trimmedSearch = search.trim();
  const params: Record<string, unknown> = { page, page_size: pageSize };
  if (trimmedSearch.length > 0) {
    params.search = trimmedSearch;
  }
  const response = await httpClient.get<PaginatedApiResource<AssistantConversationApiResource>>(
    ASSISTANT_BASE_PATH + '/conversations',
    { params }
  );
  return mapPaginatedResource(response.data, mapApiResourceToAssistantConversation);
}

export async function createAssistantConversation(): Promise<AssistantConversation> {
  const response = await httpClient.post<AssistantConversationApiResource>(
    ASSISTANT_BASE_PATH + '/conversations'
  );
  return mapApiResourceToAssistantConversation(response.data);
}

export async function deleteAssistantConversation(conversationId: string): Promise<void> {
  await httpClient.delete(ASSISTANT_BASE_PATH + '/conversations/' + conversationId);
}

export async function fetchAssistantMessages(
  conversationId: string,
  beforeSequence: number | null
): Promise<AssistantMessagePage> {
  const params: Record<string, unknown> = {};
  if (beforeSequence !== null) {
    params.before_sequence = beforeSequence;
  }
  const response = await httpClient.get<AssistantMessagePageApiResource>(
    ASSISTANT_BASE_PATH + '/conversations/' + conversationId + '/messages',
    { params }
  );
  return mapApiResourceToAssistantMessagePage(response.data);
}

export async function askAssistant(
  conversationId: string,
  question: string,
  pageContext: AssistantPageContext
): Promise<AssistantAskResult> {
  const response = await httpClient.post<AssistantAskApiResource>(
    ASSISTANT_BASE_PATH + '/conversations/' + conversationId + '/turns',
    {
      question,
      page_context: {
        route_key: pageContext.routeKey,
        entity_type: pageContext.entityType,
        entity_id: pageContext.entityId,
      },
    }
  );
  return mapApiResourceToAssistantAskResult(response.data);
}

export async function cancelAssistantTurn(turnId: string): Promise<void> {
  await httpClient.post(ASSISTANT_BASE_PATH + '/turns/' + turnId + '/cancel');
}

export async function setAssistantFeedback(
  messageId: string,
  feedback: AssistantFeedback | null,
  reason: AssistantFeedbackReason | null
): Promise<AssistantMessage> {
  const response = await httpClient.put<AssistantMessageApiResource>(
    ASSISTANT_BASE_PATH + '/messages/' + messageId + '/feedback',
    { feedback, reason }
  );
  return mapApiResourceToAssistantMessage(response.data);
}

export async function replayAssistantUiAction(
  messageId: string,
  actionId: string
): Promise<AssistantUiAction> {
  const response = await httpClient.post<{ action: AssistantUiActionApiResource }>(
    ASSISTANT_BASE_PATH + '/messages/' + messageId + '/ui-actions/' + actionId + '/replay'
  );
  return mapApiResourceToAssistantUiAction(response.data.action);
}
